import "../assets/SurveyPage.scss";

import React, {
    ChangeEvent,
    CSSProperties,
    FocusEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";

import { scrolling } from "src/common/experience";
import { FormControl, Input, Text, Textarea } from "src/sharedComponents";
import { TappableDiv } from "../../../sharedComponents/common/tappable";
import { DownArrowRightIcon } from "../../../sharedComponents/assets/icons";

import { actions } from "../actions";
import { getIsNegativeRating, getIsPositiveRating } from "../selectors/rating";
import { getSurveyAvailableTags, getSurveyData } from "../selectors/survey";

import { FeedbackList } from "./FeedbackList";

export const SurveyPage = React.memo(() => {
    const dispatch = useDispatch();

    // Optimized selectors to reduce re-renders
    const isPositiveRating = useSelector(getIsPositiveRating);
    const isNegativeRating = useSelector(getIsNegativeRating);
    const surveyData = useSelector(getSurveyData);
    const availableTags = useSelector(getSurveyAvailableTags);

    // Memoize the note based on rating type
    const note = useMemo(
        () => (isPositiveRating ? surveyData.positiveNote : surveyData.negativeNote),
        [isPositiveRating, surveyData.positiveNote, surveyData.negativeNote]
    );

    // Initialize state with memoized calculation
    const [isTagsExpanded, setIsTagsExpanded] = useState(() => availableTags.length <= 6);
    const [isInputsExpanded, setIsInputsExpanded] = useState(false);
    const [inputsHeight, setInputsHeight] = useState<number>();

    const inputsRef = useRef<HTMLDivElement>(null);

    const handleOnChangeNote = useCallback(
        (event: ChangeEvent<HTMLTextAreaElement>) => {
            const value = event.target.value;
            if (isPositiveRating) {
                dispatch(actions.setPositiveNote(value));
            } else {
                dispatch(actions.setNegativeNote(value));
            }
        },
        [dispatch, isPositiveRating]
    );

    const handleOnChangeEmail = useCallback(
        (value: string) => {
            dispatch(actions.setEmail(value));
        },
        [dispatch]
    );

    const handleOnInputFocus = useCallback((event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (event?.target) {
            scrolling.scrollElementIntoView(event.target);
        }
    }, []);

    const handleExpand = useCallback(() => {
        if (!isTagsExpanded) {
            setIsTagsExpanded(true);
        } else {
            setIsInputsExpanded(true);
        }
    }, [isTagsExpanded]);

    // Memoize computed values
    const containerClasses = useMemo(
        () =>
            classNames("survey-page__inputs-container", {
                visible: isPositiveRating,
                expanded: isInputsExpanded,
            }),
        [isPositiveRating, isInputsExpanded]
    );

    const containerStyle = useMemo(
        () => (inputsHeight ? ({ "--inputs-height": `${inputsHeight}px` } as CSSProperties) : undefined),
        [inputsHeight]
    );

    const showExpandButton = useMemo(() => !isInputsExpanded && isNegativeRating, [isInputsExpanded, isNegativeRating]);

    const expandButtonText = useMemo(() => (isTagsExpanded ? "Other" : "Show more"), [isTagsExpanded]);

    useEffect(() => {
        if (inputsRef.current && inputsRef.current.offsetHeight) {
            setInputsHeight(inputsRef.current.offsetHeight);
        }
    }, [isInputsExpanded]);

    useEffect(() => {
        let timeout: number | undefined;

        if (isInputsExpanded) {
            timeout = window.setTimeout(() => {
                const textArea = document.querySelector(".survey-page__inputs-anchor");
                if (textArea) {
                    textArea.scrollIntoView({ behavior: "smooth" });
                }
            }, 100);
        }

        return () => {
            if (timeout) {
                window.clearTimeout(timeout);
            }
        };
    }, [isInputsExpanded]);

    // Debug effect - remove in production
    // useEffect(() => {
    //     console.log("SurveyPage render:", {
    //         rating,
    //         isPositiveRating,
    //         isNegativeRating,
    //         timestamp: Date.now(),
    //     });
    // }, [rating, isPositiveRating, isNegativeRating]);

    return (
        <div className="survey__container">
            <div className="survey__content">
                <div className="survey-page">
                    {isPositiveRating ? (
                        <Text preset="g-18" mode="bold">
                            We’re glad you had a great experience!
                        </Text>
                    ) : (
                        <>
                            <Text preset="g-18" mode="bold" className="survey-page__heading">
                                Sorry to hear that. What went wrong?
                            </Text>
                            <FeedbackList showAll={isTagsExpanded} />
                        </>
                    )}
                    <div className={containerClasses} style={containerStyle}>
                        <div ref={inputsRef} className="survey-page__inputs">
                            <Text preset="g-18" mode="bold">
                                Leave a note
                            </Text>
                            <Textarea
                                value={note}
                                label="Your note"
                                placeholder="Write your comments here"
                                onChange={handleOnChangeNote}
                                onFocus={handleOnInputFocus}
                            />
                            {isNegativeRating && (
                                <FormControl message="We'll follow up to this email address">
                                    <Input
                                        allowClear
                                        type="email"
                                        value={surveyData.email}
                                        label="Your email"
                                        onChange={handleOnChangeEmail}
                                        onFocus={handleOnInputFocus}
                                    />
                                </FormControl>
                            )}
                        </div>
                    </div>
                    {showExpandButton && (
                        <TappableDiv className="survey-page__expand" onClick={handleExpand}>
                            <Text preset="g-14" mode="medium">
                                {expandButtonText}
                                <DownArrowRightIcon />
                            </Text>
                        </TappableDiv>
                    )}
                    <div className="survey-page__inputs-anchor" />
                </div>
            </div>
        </div>
    );
});

SurveyPage.displayName = "SurveyPage";
